import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from './context/ThemeContext'
import Layout from './components/Layout'
import RequireAuth from './components/RequireAuth'
import RequireProtect from './components/RequireProtect'
import ProtectedRoute from './components/ProtectedRoute'
import PageTransition from './components/PageTransition'
import HomePage from './pages/HomePage'
import SchedulePage from './pages/SchedulePage'
import ShowsPage from './pages/ShowsPage'
import TeamManagementPage from './pages/TeamManagementPage'
import TeamMemberEditPage from './pages/TeamMemberEditPage'
import SettingsPage from './pages/SettingsPage'
import LoginPage from './pages/LoginPage'
import LoginOtpPage from './pages/LoginOtpPage'
import SignupPage from './pages/signup/SignupPage'
import ProtectPage from './pages/ProtectPage'
import GoogleCalendarCallbackPage from './pages/GoogleCalendarCallbackPage'
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage'
import TermsOfServicePage from './pages/legal/TermsOfServicePage'
import AccessibilityStatementPage from './pages/legal/AccessibilityStatementPage'
import KnowledgePage from './pages/KnowledgePage'
import MusicPage from './pages/MusicPage'
import TeamHubPage from './pages/TeamHubPage'

/**
 * Redirect team members (manager/support) without a selected artist to Team Hub.
 * Regular artists go to /shows.
 */
function SmartRedirect() {
  try {
    const raw = localStorage.getItem('dizeeUser')
    const user = raw ? JSON.parse(raw) : null
    const isTeamMember = user?.professionRole && ['manager', 'support'].includes(user.professionRole)
    const hasSelectedArtist = !!localStorage.getItem('selectedArtist')

    if (isTeamMember && !hasSelectedArtist) {
      return <Navigate to="/team-hub" replace />
    }
  } catch {
    // fall through to default
  }
  return <Navigate to="/shows" replace />
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Homepage */}
          <Route
            path="/home"
            element={<HomePage />}
          />

          {/* Public Legal Pages */}
          <Route
            path="/privacy-policy"
            element={
              <PageTransition>
                <PrivacyPolicyPage />
              </PageTransition>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <PageTransition>
                <TermsOfServicePage />
              </PageTransition>
            }
          />
          <Route
            path="/accessibility-statement"
            element={
              <PageTransition>
                <AccessibilityStatementPage />
              </PageTransition>
            }
          />

          {/* Auth Pages */}
          <Route
            path="/protect"
            element={
              <PageTransition>
                <ProtectPage />
              </PageTransition>
            }
          />
          <Route
            path="/login"
            element={
              <RequireProtect>
                <PageTransition>
                  <LoginPage />
                </PageTransition>
              </RequireProtect>
            }
          />
          <Route
            path="/login-otp"
            element={
              <RequireProtect>
                <PageTransition>
                  <LoginOtpPage />
                </PageTransition>
              </RequireProtect>
            }
          />
          <Route
            path="/signup"
            element={
              <RequireProtect>
                <PageTransition>
                  <SignupPage />
                </PageTransition>
              </RequireProtect>
            }
          />

          {/* Authenticated App — Sidebar Layout */}
          <Route
            path="/"
            element={
              <RequireProtect>
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              </RequireProtect>
            }
          >
            <Route index element={<SmartRedirect />} />
            <Route path="team-hub" element={<TeamHubPage />} />
            <Route path="schedule" element={<ProtectedRoute requiredPermission="schedule"><SchedulePage /></ProtectedRoute>} />
            <Route path="shows" element={<ProtectedRoute requiredPermission="shows"><ShowsPage /></ProtectedRoute>} />
            <Route path="knowledge" element={<ProtectedRoute requiredPermission="knowledge"><KnowledgePage /></ProtectedRoute>} />
            <Route path="music" element={<ProtectedRoute requiredPermission="music"><MusicPage /></ProtectedRoute>} />
            <Route path="team" element={<ProtectedRoute requiredPermission="team"><TeamManagementPage /></ProtectedRoute>} />
            <Route path="team/:teamMemberId/edit" element={<ProtectedRoute requiredPermission="team"><TeamMemberEditPage /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute requiredPermission="settings"><SettingsPage /></ProtectedRoute>} />
            <Route path="google-calendar-callback" element={<ProtectedRoute requiredPermission="schedule"><GoogleCalendarCallbackPage /></ProtectedRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
